import { useMutation, useQuery } from '@apollo/client';
import { Alert, Button, NumberInput, Stack, Textarea } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconCurrencyEuro } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import {
  bulkUpdateProductPriceDocument,
  countActiveCartsForProductDocument,
} from '../graphql/mutations';

interface Props {
  productId: string;
  code: string;
  name: string;
  currentPrice: number;
}

const buildMessage = (code: string, name: string, oldPrice: number, newPrice: number) =>
  `Cena produktu ${code} (${name}) bola zmenená z ${oldPrice} € na ${newPrice} €.`;

export const BulkPriceEditModal = ({ productId, code, name, currentPrice }: Props) => {
  const [newPrice, setNewPrice] = useState<number>(currentPrice);
  const [message, setMessage] = useState<string>(
    buildMessage(code, name, currentPrice, currentPrice)
  );

  useEffect(() => {
    setMessage(buildMessage(code, name, currentPrice, newPrice));
  }, [code, name, currentPrice, newPrice]);

  const { data: cartsData } = useQuery(countActiveCartsForProductDocument, {
    variables: { productId },
    fetchPolicy: 'network-only',
  });

  const affectedCarts = cartsData?.countOrders ?? 0;

  const [bulkUpdate, { loading }] = useMutation(bulkUpdateProductPriceDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    notifyOnNetworkStatusChange: true,
  });

  const priceUnchanged = newPrice === currentPrice;

  const alertColor = newPrice < currentPrice ? 'orange' : 'blue';

  const handleSubmit = async () => {
    const result = await bulkUpdate({
      variables: { productId, newPrice, message },
    });

    const count = result.data?.bulkUpdateProductPrice ?? 0;

    notifications.show({
      message: `Cena upravená pre ${count} košíkov`,
      color: 'green',
    });

    modals.closeAll();
  };

  return (
    <Stack>
      <Alert color={alertColor} variant="light">
        Aktuálna cena: <strong>{currentPrice} €</strong>. {affectedCarts} prebiehajúcich košíkov
        (CREATED) bude prepočítaných.
      </Alert>
      <NumberInput
        label="Nová cena"
        value={newPrice}
        onChange={(value) => setNewPrice(typeof value === 'number' ? value : 0)}
        allowNegative={false}
        allowDecimal={false}
        hideControls
        suffix=" €"
      />
      <Textarea
        label="Správa pre zákazníka"
        description='K správe sa automaticky pripojí riadok "Celková suma objednávky: X €" pre každého zákazníka.'
        autosize
        minRows={3}
        maxRows={6}
        value={message}
        onChange={(e) => setMessage(e.currentTarget.value)}
      />
      <Button
        leftSection={<IconCurrencyEuro size={16} />}
        loading={loading}
        disabled={priceUnchanged || message.length === 0}
        onClick={handleSubmit}
      >
        Upraviť cenu a notifikovať
      </Button>
    </Stack>
  );
};
